import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Edit, Upload, Trash2, Link } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const RoomManagement = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [imageUrlInput, setImageUrlInput] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Extract Google Drive file ID from various URL formats
  const extractGoogleDriveFileId = (url) => {
    if (!url) return null;
    let fileId = null;
    // Format: drive.google.com/uc?id=FILE_ID or drive.google.com/thumbnail?id=FILE_ID
    if (url.includes('drive.google.com/uc') || url.includes('drive.google.com/thumbnail')) {
      const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (idMatch) fileId = idMatch[1];
    }
    // Format: drive.google.com/file/d/FILE_ID/view
    const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (fileIdMatch) fileId = fileIdMatch[1];
    // Format: drive.google.com/open?id=FILE_ID
    const openIdMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (!fileId && openIdMatch) fileId = openIdMatch[1];
    return fileId;
  };

  // Convert Google Drive URL to embeddable image URL
  const convertGoogleDriveImageUrl = (url) => {
    if (!url) return url;
    const fileId = extractGoogleDriveFileId(url);
    if (fileId) {
      return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
    }
    return url;
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=800';
    // Handle Google Drive URLs - convert to embeddable format
    if (imageUrl.includes('drive.google.com')) {
      return convertGoogleDriveImageUrl(imageUrl);
    }
    // If it's a relative URL (starts with /uploads), convert to API route
    if (imageUrl.startsWith('/uploads/')) {
      return `${BACKEND_URL}/api${imageUrl}`;
    }
    // If it starts with http, return as is (external URL like Unsplash)
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    // Default fallback
    return `${BACKEND_URL}/api${imageUrl}`;
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/public/rooms`);
      if (response.data.success) {
        setRooms(response.data.rooms);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
      toast.error('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRoom = (room) => {
    setSelectedRoom({ ...room });
    setImageUrlInput('');
    setEditDialogOpen(true);
  };

  // Apply URL from input to room image
  const handleApplyImageUrl = () => {
    if (!imageUrlInput.trim()) {
      toast.error('Please enter a valid URL');
      return;
    }
    // Convert Google Drive URLs to embeddable format for storage
    const convertedUrl = convertGoogleDriveImageUrl(imageUrlInput.trim());
    setSelectedRoom({ ...selectedRoom, image_url: convertedUrl });
    toast.success('Image URL applied! Click "Update Room" to save.');
    setImageUrlInput('');
  };

  const handleUpdateRoom = async () => {
    try {
      await axios.put(`${API}/admin/rooms/${selectedRoom.id}`, {
        name: selectedRoom.name,
        description: selectedRoom.description,
        max_guests: parseInt(selectedRoom.max_guests),
        base_price: parseFloat(selectedRoom.base_price),
        image_url: selectedRoom.image_url,
        amenities: selectedRoom.amenities,
        available: selectedRoom.available,
        // Room + Meal pricing
        meal_price_2_persons: parseFloat(selectedRoom.meal_price_2_persons || 1600),
        meal_price_3_persons: parseFloat(selectedRoom.meal_price_3_persons || 1500),
        meal_price_4_plus: parseFloat(selectedRoom.meal_price_4_plus || 1400),
        min_occupancy: parseInt(selectedRoom.min_occupancy || 2)
      });
      
      toast.success('Room updated successfully!');
      setEditDialogOpen(false);
      fetchRooms();
    } catch (error) {
      console.error('Error updating room:', error);
      toast.error('Failed to update room');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('media_type', 'photo');

      toast.info('Uploading photo...');
      
      const response = await axios.post(`${API}/media/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // Use the file_url directly (relative path)
        const imageUrl = response.data.media.file_url;
        setSelectedRoom({ ...selectedRoom, image_url: imageUrl });
        toast.success('Photo uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading rooms...</div>;
  }

  return (
    <div className="space-y-6" data-testid="room-management-container">
      <Toaster />
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Room Management</h1>
        <p className="text-gray-600 mt-1">Manage room details, prices, and photos</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {rooms.map((room) => (
          <Card key={room.id} className="hover:shadow-lg transition-shadow" data-testid={`room-card-${room.id}`}>
            <CardHeader className="p-0">
              <img 
                src={getImageUrl(room.image_url)} 
                alt={room.name}
                className="w-full h-48 object-cover rounded-t-lg"
              />
            </CardHeader>
            <CardContent className="p-4">
              <CardTitle className="text-lg mb-2">{room.name}</CardTitle>
              <p className="text-sm text-gray-600 mb-3">{room.description}</p>
              
              <div className="space-y-2 text-sm mb-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Max Guests:</span>
                  <span className="font-semibold">{room.max_guests}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Room Only:</span>
                  <span className="font-semibold text-green-700">₹{room.base_price}/night</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-semibold ${room.available ? 'text-green-600' : 'text-red-600'}`}>
                    {room.available ? 'Available' : 'Unavailable'}
                  </span>
                </div>
              </div>

              {room.amenities && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Amenities:</p>
                  <div className="flex flex-wrap gap-1">
                    {room.amenities.slice(0, 3).map((amenity, idx) => (
                      <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <Button 
                onClick={() => handleEditRoom(room)}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid={`edit-room-${room.id}`}
              >
                <Edit className="mr-2" size={16} />
                Edit Room
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Room Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Room Details</DialogTitle>
          </DialogHeader>
          {selectedRoom && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="room_name">Room Name</Label>
                <Input
                  id="room_name"
                  value={selectedRoom.name}
                  onChange={(e) => setSelectedRoom({ ...selectedRoom, name: e.target.value })}
                  data-testid="room-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={selectedRoom.description}
                  onChange={(e) => setSelectedRoom({ ...selectedRoom, description: e.target.value })}
                  rows={3}
                  data-testid="room-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="max_guests">Max Guests</Label>
                  <Input
                    id="max_guests"
                    type="number"
                    value={selectedRoom.max_guests}
                    onChange={(e) => setSelectedRoom({ ...selectedRoom, max_guests: e.target.value })}
                    data-testid="max-guests-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="base_price">Room Only Price (₹/night)</Label>
                  <Input
                    id="base_price"
                    type="number"
                    value={selectedRoom.base_price}
                    onChange={(e) => setSelectedRoom({ ...selectedRoom, base_price: e.target.value })}
                    data-testid="base-price-input"
                  />
                </div>
              </div>

              {/* Note about meal pricing */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Room + Food pricing is managed globally for all rooms. 
                  Go to <span className="font-semibold">Room + Food Pricing</span> in the sidebar to update meal rates.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="room_photo">Room Photo</Label>
                
                {/* Current Photo Preview */}
                {selectedRoom.image_url && (
                  <div className="mb-3">
                    <img 
                      src={getImageUrl(selectedRoom.image_url)}
                      alt="Current room photo"
                      className="w-full h-48 object-cover rounded-lg border"
                    />
                  </div>
                )}
                
                {/* Option 1: Add from URL (Recommended for Production) */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <Label className="text-blue-800 font-medium flex items-center gap-2 mb-2">
                    <Link size={16} />
                    Add from URL (Recommended for Live Site)
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Paste Google Drive or image URL"
                      value={imageUrlInput}
                      onChange={(e) => setImageUrlInput(e.target.value)}
                      className="flex-1 bg-white"
                      data-testid="room-image-url-input"
                    />
                    <Button
                      type="button"
                      onClick={handleApplyImageUrl}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="apply-image-url-button"
                    >
                      Apply
                    </Button>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    💡 Use Google Drive links for images that will work on the live deployed site
                  </p>
                </div>
                
                {/* Option 2: Upload from Computer (Preview Only) */}
                <div className="border border-gray-200 rounded-lg p-3">
                  <Label className="text-gray-700 font-medium flex items-center gap-2 mb-2">
                    <Upload size={16} />
                    Upload from Computer (Preview Only)
                  </Label>
                  <label htmlFor="photo-upload" className="block">
                    <div className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors">
                      <Upload size={20} className="text-gray-600" />
                      <span className="text-sm text-gray-700">Click to upload photo</span>
                    </div>
                  </label>
                  <input
                    id="photo-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoUpload}
                    data-testid="room-photo-upload"
                  />
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ Uploaded files work in preview but may not appear on the deployed site
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="amenities">Amenities (comma-separated)</Label>
                <Input
                  id="amenities"
                  value={selectedRoom.amenities ? selectedRoom.amenities.join(', ') : ''}
                  onChange={(e) => setSelectedRoom({ 
                    ...selectedRoom, 
                    amenities: e.target.value.split(',').map(item => item.trim()) 
                  })}
                  placeholder="Mountain View, WiFi, Hot Water"
                  data-testid="amenities-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Availability</Label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={selectedRoom.available === true}
                      onChange={() => setSelectedRoom({ ...selectedRoom, available: true })}
                      data-testid="available-yes"
                    />
                    <span>Available</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={selectedRoom.available === false}
                      onChange={() => setSelectedRoom({ ...selectedRoom, available: false })}
                      data-testid="available-no"
                    />
                    <span>Unavailable</span>
                  </label>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm text-green-800 mb-2">
                  <strong>💡 How to update room photo:</strong>
                </p>
                <div className="text-xs text-green-700 space-y-2">
                  <div>
                    <p className="font-semibold">Option 1: Google Drive URL (Recommended)</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Upload image to Google Drive</li>
                      <li>Right-click → Share → "Anyone with link can view"</li>
                      <li>Copy the link and paste in the URL field above</li>
                      <li>Click "Apply" then "Update Room"</li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-semibold">Option 2: Direct Upload (Preview Only)</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Click "Upload from Computer"</li>
                      <li>Select a photo (JPG, PNG, WEBP, max 5MB)</li>
                      <li>Click "Update Room" to save</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleUpdateRoom}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="update-room-button"
                >
                  Update Room
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoomManagement;
